import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/TasksPage.css';
import { CustomModal } from '../components/CustomModal';
import { BackgroundStars } from '../components/BackgroundStars';
import { NavigationBar } from '../components/NavigationBar';
import { GenderModal } from '../components/GenderModal';
import { useGameStore } from '../store/gameStore';
import { FlayerTask, SubgramTask, tasksAPI, subgramAPI, traffyAPI, TraffyTask, CustomTask, customTasksAPI, tgrassAPI, TgrassOffer, TaddyFeedItem, taddyAPI, userAPI } from '../services/api';
import UniversalTaskCard from '../components/UniversalTaskCard';
import RewardPopup from '../components/RewardPopup';

interface TaskCardProps {
  type: 'exclusive' | 'regular';
  title: string;
  description: string;
  reward: number;
  progress?: {
    current: number;
    total: number;
  };
  icon?: React.ReactNode;
  buttonText?: string;
  onClick: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  type, title, description, reward, progress, icon, buttonText, onClick 
}) => {
  const buttonClassName = type === 'exclusive' ? 'task-card-button animate-pulse' : 'task-card-button-gray';
  const cardClassName = `task-card ${type === 'exclusive' ? 'task-card-exclusive' : 'task-card-regular'}`;
  
  return (
    <div className={cardClassName}>
      <div className="task-card-header">
        {type === 'exclusive' && (
          <div className="task-card-tag">
            <span className="fire-icon">🔥</span>
            <span>ЭКСКЛЮЗИВ</span>
          </div>
        )}
        {type === 'regular' && (
          <div className="task-card-title">
            {title}
          </div>
        )}
        <div className={`task-card-reward ${type === 'regular' ? 'reward-regular' : ''}`}>{reward} <span className="reward-star">⭐</span></div>
      </div>
      
      <div className="task-card-description">
        {description}
      </div>
      
      <div className="task-card-features">
        <div className="task-feature">✓ Эксклюзив</div>
      </div>
      
      <button className={buttonClassName} onClick={onClick}>
        {buttonText || (type === 'exclusive' ? 'ПОЛУЧИТЬ' : 'Перейти')}
      </button>
      
      {progress && (
        <>
          <div className="task-progress-bar">
            <div 
              className="task-progress-fill" 
              style={{ 
                width: `${(progress.current / progress.total) * 100}%`,
                background: 'linear-gradient(90deg, #3498db, #2ecc71)'
              }} 
            />
          </div>
          <div className="task-progress-text">
            <span>Осталось {progress.total - progress.current} из {progress.total}</span>
            <span className="task-progress-hurry">⚡ Успей получить</span>
          </div>
        </>
      )}
    </div>
  );
};

interface ExclusiveSectionProps {
  onPromoClick: () => void;
  onPrimaryTaskClick: () => void;
  onSecondaryTaskClick: () => void;
}

const ExclusiveSection: React.FC<ExclusiveSectionProps> = ({
  onPromoClick,
  onPrimaryTaskClick,
  onSecondaryTaskClick,
}) => (
  <>
    <h2 className="section-title">Эксклюзив</h2>
    <button className="tasks-promo-banner" onClick={onPromoClick} type="button">
      <div className="tasks-promo-content">
        <div className="tasks-promo-title">✨ Получи 1000 звёзд Telegram!</div>
        <div className="tasks-promo-subtitle">Нажми, чтобы узнать как!</div>
      </div>
      <img
        className="tasks-promo-image"
        src="/img/Plush%20Pepe.webp"
        alt="Plush Pepe"
      />
    </button>
    <TaskCard
      type="exclusive"
      title="ЭКСКЛЮЗИВ"
      description="Узнай, как получить 1000 звезд бесплатно"
      reward={1000}
      progress={{ current: 541, total: 1000 }}
      buttonText="ПОЛУЧИТЬ"
      onClick={onPrimaryTaskClick}
    />
    <TaskCard
      type="exclusive"
      title="ЭКСКЛЮЗИВ"
      description="Заработай на 1000 звезд telegram"
      reward={1000}
      buttonText="ПЕРЕЙТИ"
      onClick={onSecondaryTaskClick}
    />
  </>
);

const TasksPage: React.FC = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [genderModalOpen, setGenderModalOpen] = useState(false);
  const [tasksVisible, setTasksVisible] = useState(true);
  const [flayerTasks, setFlayerTasks] = useState<FlayerTask[]>([]);
  const [subgramTasks, setSubgramTasks] = useState<SubgramTask[]>([]);
  const [traffyTasks, setTraffyTasks] = useState<TraffyTask[]>([]);
  const [customTasks, setCustomTasks] = useState<CustomTask[]>([]);
  const [tgrassTasks, setTgrassTasks] = useState<TgrassOffer[]>([]);
  const [taddyTasks, setTaddyTasks] = useState<TaddyFeedItem[]>([]);
  const [recentCompletedSubgram, setRecentCompletedSubgram] = useState<Set<string>>(new Set());
  const [isLoadingFlayer, setIsLoadingFlayer] = useState(false);
  const [isLoadingSubgram, setIsLoadingSubgram] = useState(false);
  const [isLoadingTraffy, setIsLoadingTraffy] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isLoadingTgrass, setIsLoadingTgrass] = useState(false);
  const [isLoadingTaddy, setIsLoadingTaddy] = useState(false);
  const [flayerError, setFlayerError] = useState<string | null>(null);
  const [subgramError, setSubgramError] = useState<string | null>(null);
  const [showExclusiveReward, setShowExclusiveReward] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    buttons: [] as Array<{id: string, text: string, type?: string}>,
    onClose: (id: string) => {}
  });
  const [activeTab, setActiveTab] = useState<'flayer' | 'subgram' | 'traffy'>('flayer');
  const [showReward, setShowReward] = useState(false);
  const [rewardAmount, setRewardAmount] = useState(0);
  const [adRewardOpen, setAdRewardOpen] = useState(false);
  const [adRewardAmount, setAdRewardAmount] = useState(0);
  const [monetagPopupAvailable, setMonetagPopupAvailable] = useState(false);
  const [gigaOfferwallAvailable, setGigaOfferwallAvailable] = useState(false);
  const [isLoadingGigaOfferwall, setIsLoadingGigaOfferwall] = useState(false);
  const gigaOfferwallRef = React.useRef<any | null>(null);
  const adsgramControllerRef = React.useRef<any | null>(null);
  
  const navigate = useNavigate();
  const { stars, tickets, addStars, gender, hasGender, fetchGender, setGender, setStars } = useGameStore();
  
  useEffect(() => {
    try {
      const Adsgram: any = (window as any).Adsgram;
      if (Adsgram && typeof Adsgram.init === 'function') {
        adsgramControllerRef.current = Adsgram.init({ blockId: 'int-23499' });
      }
    } catch (e) {
      // ignore Adsgram init errors, we'll fallback in handleWatchAd
    }
  }, []);

  const checkGigaPendingRewards = async (sdk: any) => {
    if (!sdk || typeof sdk.pending !== 'function') {
      return;
    }
    try {
      const pendingRewards = await sdk.pending();
      if (!Array.isArray(pendingRewards) || pendingRewards.length === 0) {
        return;
      }

      for (const reward of pendingRewards) {
        const amount = typeof reward?.amount === 'number' ? reward.amount : 0;
        if (!amount || amount <= 0) continue;

        try {
          await userAPI.updateResources({ stars: amount, source: 'giga_offerwall' });
          addStars(amount);
        } catch (e) {
        }
      }
    } catch (e) {
    }
  };

  const initGigaOfferwall = async () => {
    if (gigaOfferwallRef.current || isLoadingGigaOfferwall) {
      return;
    }
    setIsLoadingGigaOfferwall(true);
    try {
      if (!(window as any).loadOfferWallSDK) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.giga.pub/script/offer/loader/loader.js';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Giga Offerwall loader'));
          document.head.appendChild(script);
        });
      }

      const loadOfferWallSDK = (window as any).loadOfferWallSDK;
      if (typeof loadOfferWallSDK !== 'function') {
        return;
      }

      const telegramId = localStorage.getItem('telegram_id') || undefined;
      const sdk = await loadOfferWallSDK({ projectId: '5098', userId: telegramId });

      gigaOfferwallRef.current = sdk;

      if (sdk && typeof sdk.on === 'function') {
        try {
          sdk.on('closed', async () => {
            await checkGigaPendingRewards(sdk);
          });
        } catch (e) {}
      }

      if (typeof sdk.hasOffers === 'function') {
        const hasOffers = sdk.hasOffers();
        setGigaOfferwallAvailable(!!hasOffers);
      } else {
        setGigaOfferwallAvailable(true);
      }

      await checkGigaPendingRewards(sdk);
    } catch (e) {
      setGigaOfferwallAvailable(false);
    } finally {
      setIsLoadingGigaOfferwall(false);
    }
  };

  // Просмотр вознаграждаемой рекламы (Adsgram -> Giga -> Monetag)
  const handleWatchAd = async () => {
    // 1. Основной источник — Adsgram Rewarded / Interstitial
    let adsgramRewarded = false;
    try {
      const controller = adsgramControllerRef.current;
      if (controller && typeof controller.show === 'function') {
        await controller
          .show()
          .then(() => {
            const rw = 10;
            userAPI
              .updateResources({ stars: rw, source: 'adsgram_reward' })
              .catch((e) => console.warn('[Adsgram] failed to persist reward on backend', e));
            addStars(rw);
            setAdRewardAmount(rw);
            setAdRewardOpen(true);
            adsgramRewarded = true;
          })
          .catch((result: any) => {
            console.warn('[Adsgram] show failed or no fill', result);
          });
      }
    } catch (e) {
      console.warn('[Adsgram] Rewarded/Interstitial failed', e);
    }

    if (adsgramRewarded) {
      return;
    }

    // 2. Дополнительный источник — Giga Rewarded Video (showGiga)
    let gigaRewarded = false;
    try {
      const showGiga: any = (window as any).showGiga;
      if (typeof showGiga === 'function') {
        const GIGA_TIMEOUT_MS = 8000;

        // Аналогичный таймаут для защиты от вечного pending
        await Promise.race([
          showGiga(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Giga rewarded timeout')), GIGA_TIMEOUT_MS)
          ),
        ]);

        const rw = 10;
        try {
          await userAPI.updateResources({ stars: rw, source: 'giga_reward' });
        } catch (e) {
          console.warn('[Giga] failed to persist reward on backend', e);
        }

        addStars(rw);
        setAdRewardAmount(rw);
        setAdRewardOpen(true);
        gigaRewarded = true;
      }
    } catch (e) {
      console.warn('[Giga] Rewarded video failed', e);
    }

    // Если Giga успешно выдал награду, до Monetag не доходим
    if (gigaRewarded) {
      return;
    }

    // 3. Fallback — Monetag Rewarded Interstitial (show_10641264)
    try {
      const showMonetag: any = (window as any).show_10641264;
      if (typeof showMonetag !== 'function') {
        console.warn('[Monetag] show_10641264 is not available');
        return;
      }

      const ymid = localStorage.getItem('telegram_id') || undefined;

      // Прелоад, затем показ
      await showMonetag({ type: 'preload', ymid });
      await showMonetag({ ymid });

      const rw = 10;
      try {
        await userAPI.updateResources({ stars: rw, source: 'monetag_reward' });
      } catch (e) {
        console.warn('[Monetag] failed to persist reward on backend', e);
      }
      addStars(rw);
      setAdRewardAmount(rw);
      setAdRewardOpen(true);
    } catch (e) {
      console.warn('[Monetag] Rewarded Interstitial failed', e);
    }
  };
  
  const handleMonetagPopupTask = async () => {
    try {
      const showMonetag: any = (window as any).show_10641264;
      if (typeof showMonetag !== 'function') {
        console.warn('[Monetag Popup] show_10641264 is not available');
        return;
      }

      const ymid = localStorage.getItem('telegram_id') || undefined;
      await showMonetag({ ymid });

      const rw = 10;
      try {
        await userAPI.updateResources({ stars: rw, source: 'monetag_popup_reward' });
      } catch (e) {
        console.warn('[Monetag Popup] failed to persist reward on backend', e);
      }
      addStars(rw);
      setAdRewardAmount(rw);
      setAdRewardOpen(true);
    } catch (e) {
      console.warn('[Monetag Popup] failed', e);
    }
  };
  
  // Загрузка заданий Flayer
  const loadFlayerTasks = async () => {
    setIsLoadingFlayer(true);
    setFlayerError(null);
    
    try {
      const response = await tasksAPI.getFlayerTasks();
      if (response && response.success) {
        setFlayerTasks(response.tasks || []);
      } else {
        setFlayerError('Не удалось загрузить задания Flayer');
      }
    } catch (error) {
      console.error('Error loading Flayer tasks:', error);
      setFlayerError('Ошибка при загрузке заданий Flayer');
    } finally {
      setIsLoadingFlayer(false);
    }
  };
  
  // Загрузка заданий Subgram
  const loadSubgramTasks = async () => {
    setIsLoadingSubgram(true);
    setSubgramError(null);
    
    try {
      const response = await subgramAPI.getSubgramTasks();
      if (response && response.success) {
        setSubgramTasks(response.tasks || []);
      } else {
        setSubgramError('Не удалось загрузить задания Subgram');
      }
    } catch (error) {
      console.error('Error loading Subgram tasks:', error);
      setSubgramError('Ошибка при загрузке заданий Subgram');
    } finally {
      setIsLoadingSubgram(false);
    }
  };
  
  // Перезагрузка только пользовательских заданий
  const reloadCustomTasks = async () => {
    setIsLoadingCustom(true);
    try {
      const response = await customTasksAPI.getCustomTasks();
      if (response && response.success) {
        setCustomTasks(response.tasks || []);
      } else {
        setCustomTasks([]);
      }
    } catch (e) {
      setCustomTasks([]);
    } finally {
      setIsLoadingCustom(false);
    }
  };

  // Перезагрузка только Tgrass
  const reloadTgrassTasks = async () => {
    setIsLoadingTgrass(true);
    try {
      const response = await tgrassAPI.getTgrassOffers();
      if (response && response.success) {
        setTgrassTasks(response.offers || []);
      } else {
        setTgrassTasks([]);
      }
    } catch (e) {
      setTgrassTasks([]);
    } finally {
      setIsLoadingTgrass(false);
    }
  };

  // Перезагрузка только Traffy
  const reloadTraffyTasks = async () => {
    setIsLoadingTraffy(true);
    try {
      const response = await traffyAPI.getTraffyTasks();
      if (response && response.success) {
        setTraffyTasks(response.tasks || []);
      } else {
        setTraffyTasks([]);
      }
    } catch (e) {
      setTraffyTasks([]);
    } finally {
      setIsLoadingTraffy(false);
    }
  };

  // Перезагрузка только Taddy
  const reloadTaddyTasks = async () => {
    setIsLoadingTaddy(true);
    try {
      const Taddy: any = (window as any).Taddy;
      const exchange = Taddy && typeof Taddy.exchange === 'function' ? Taddy.exchange() : null;
      if (!exchange) {
        setTaddyTasks([]);
        return;
      }
      const items = await exchange.feed({ limit: 10, imageFormat: 'webp', autoImpressions: true, showCompleted: false });
      setTaddyTasks(items || []);
    } catch (e) {
      setTaddyTasks([]);
    } finally {
      setIsLoadingTaddy(false);
    }
  };
  
  // Функция для загрузки всех заданий (независимо друг от друга)
  const loadTasks = async () => {
    // Flayer
    setIsLoadingFlayer(true);
    const loadFlayerTasksPromise = tasksAPI.getFlayerTasks()
      .then(response => {
        if (response.success) {
          setFlayerTasks(response.tasks);
        } else {
          console.error('Ошибка при загрузке заданий Flayer:', response.error);
        }
      })
      .catch(error => {
        console.error('Ошибка при загрузке заданий Flayer:', error);
      })
      .finally(() => setIsLoadingFlayer(false));

    // Subgram
    setIsLoadingSubgram(true);
    const loadSubgramTasksPromise = subgramAPI.getSubgramTasks()
      .then(response => {
        if (response.success) {
          setSubgramTasks(response.tasks);
        } else {
          console.error('Ошибка при загрузке заданий Subgram:', response.error);
        }
      })
      .catch(error => {
        console.error('Ошибка при загрузке заданий Subgram:', error);
      })
      .finally(() => setIsLoadingSubgram(false));
    
    // Traffy
    setIsLoadingTraffy(true);
    const loadTraffyTasksPromise = traffyAPI.getTraffyTasks()
      .then(response => {
        if (response.success) {
          setTraffyTasks(response.tasks);
        } else {
          console.error('Ошибка при загрузке заданий Traffy:', response.error);
          setTraffyTasks([]);
        }
      })
      .catch(error => {
        console.error('Ошибка при загрузке заданий Traffy:', error);
        setTraffyTasks([]);
      })
      .finally(() => setIsLoadingTraffy(false));

    // Custom tasks
    setIsLoadingCustom(true);
    const loadCustomTasksPromise = customTasksAPI.getCustomTasks()
      .then(response => {
        if (response.success) {
          setCustomTasks(response.tasks);
        } else {
          console.error('Ошибка при загрузке пользовательских заданий:', response);
          setCustomTasks([]);
        }
      })
      .catch(error => {
        console.error('Ошибка при загрузке пользовательских заданий:', error);
        setCustomTasks([]);
      })
      .finally(() => setIsLoadingCustom(false));

    // Tgrass
    setIsLoadingTgrass(true);
    const loadTgrassTasksPromise = tgrassAPI.getTgrassOffers()
      .then(response => {
        if (response.success) {
          setTgrassTasks(response.offers || []);
        } else {
          console.error('Ошибка при загрузке заданий Tgrass:', response);
          setTgrassTasks([]);
        }
      })
      .catch(error => {
        console.error('Ошибка при загрузке заданий Tgrass:', error);
        setTgrassTasks([]);
      })
      .finally(() => setIsLoadingTgrass(false));

    // Taddy via SDK
    setIsLoadingTaddy(true);
    const loadTaddyTasksPromise = (async () => {
      try {
        const Taddy: any = (window as any).Taddy;
        const exchange = Taddy && typeof Taddy.exchange === 'function' ? Taddy.exchange() : null;
        if (!exchange) {
          setTaddyTasks([]);
          return;
        }
        const items = await exchange.feed({ limit: 10, imageFormat: 'webp', autoImpressions: true, showCompleted: false });
        setTaddyTasks(items || []);
      } catch (err) {
        console.error('[Taddy] feed error', err);
        setTaddyTasks([]);
      } finally {
        setIsLoadingTaddy(false);
      }
    })();

    // Не дожидаемся всех результатов, даем разделам грузиться независимо
    void Promise.allSettled([
      loadFlayerTasksPromise,
      loadSubgramTasksPromise,
      loadTraffyTasksPromise,
      loadCustomTasksPromise,
      loadTgrassTasksPromise,
      loadTaddyTasksPromise
    ]);
  };
  
  useEffect(() => {
    // Ранее здесь проверялся пол пользователя и открывалось модальное окно выбора пола.
    // Сейчас сразу показываем задания и загружаем все источники без запроса пола.
    setTasksVisible(true);
    loadTasks();
  }, []);

  useEffect(() => {
    if (tasksVisible) {
      void initGigaOfferwall();
    }
  }, [tasksVisible]);

  useEffect(() => {
    const checkMonetagPopup = async () => {
      try {
        const showMonetag: any = (window as any).show_10641264;
        if (typeof showMonetag !== 'function') {
          setMonetagPopupAvailable(false);
          return;
        }
        const ymid = localStorage.getItem('telegram_id') || undefined;

        // Прелоад, затем показ
        await showMonetag({ type: 'preload', ymid });
        setMonetagPopupAvailable(true);
      } catch (e) {
        setMonetagPopupAvailable(false);
      }
    };

    checkMonetagPopup();
  }, []);
  
  const handleFlayerTaskComplete = () => {
    // Перезагружаем список заданий после выполнения
    loadFlayerTasks();
  };
  
  const handleSubgramTaskComplete = (task?: SubgramTask) => {
    // Сразу скрываем выполненную задачу локально, чтобы UI обновился детерминированно
    if (task && task.link) {
      setRecentCompletedSubgram(prev => {
        const next = new Set(prev);
        next.add(task.link);
        return next;
      });
    }
    // И параллельно перезагружаем список заданий Subgram
    loadSubgramTasks();
  };
  
  const showPopup = (params: any) => {
    return new Promise<string>((resolve) => {
      setModalConfig({
        title: 'Уведомление',
        message: params.message,
        buttons: params.buttons || [],
        onClose: (buttonId: string) => {
          setModalIsOpen(false);
          resolve(buttonId);
        }
      });
      setModalIsOpen(true);
    });
  };
  
  const handleExclusiveTask = () => {
    const url = 'https://t.me/your_best_present_bot/tappy';
    try {
      const destination: 'offerist' | 'gamesport' = 'offerist';
      userAPI.trackExternalClick(destination).catch(() => {});
    } catch {}
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg && typeof tg.openTelegramLink === 'function') {
        tg.openTelegramLink(url);
        return;
      }
    } catch (e) {
      // ignore and fallback
    }
    window.open(url, '_blank');
  };

  const handleExclusiveEarnings = () => {
    const url = 'https://t.me/your_stars_presents_bot/app';
    try {
      const destination: 'offerist' | 'gamesport' = 'gamesport';
      userAPI.trackExternalClick(destination).catch(() => {});
    } catch {}
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg && typeof tg.openTelegramLink === 'function') {
        tg.openTelegramLink(url);
        return;
      }
    } catch (e) {}
    window.open(url, '_blank');
  };
  
  const handleCloseExclusiveReward = () => {
    setShowExclusiveReward(false);
  };
  
  // Обработчик выбора пола
  const handleGenderSelect = async (selectedGender: 'male' | 'female') => {
    const success = await setGender(selectedGender);
    if (success) {
      setGenderModalOpen(false);
      setTasksVisible(true);
      // Используем новую функцию загрузки всех заданий независимо
      loadTasks();
    } else {
      showPopup({
        message: "Произошла ошибка при сохранении. Пожалуйста, попробуйте еще раз.",
        buttons: [{ id: "ok", type: "default", text: "OK" }]
      });
    }
  };
  
  // Объединяем статусы загрузки всех источников
  const isAnyLoading = isLoadingFlayer || isLoadingSubgram || isLoadingTraffy || isLoadingCustom || isLoadingTgrass || isLoadingTaddy;
  
  // Проверяем наличие ошибок (по ключевым источникам)
  const hasAnyError = !!(flayerError || subgramError);
  
  // Сообщение об ошибке
  const errorMessage = flayerError || subgramError || "Не удалось загрузить задания";
  
  // Проверяем, есть ли хоть какие-то задания для отображения
  const hasAnyTasks = flayerTasks.length > 0 || subgramTasks.length > 0 || traffyTasks.length > 0 || customTasks.length > 0 || tgrassTasks.length > 0 || taddyTasks.length > 0;

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="app-title">SaperStars</div>
        <div className="app-score">
          <div className="score-group">
            <span className="score-value">{tickets}</span>
            <span className="score-icon">🎟️</span>
          </div>
          <div className="score-group">
            <span className="score-value">{stars.toFixed(1)}</span>
            <span className="score-icon">⭐</span>
          </div>
        </div>
      </header>

      <div className="content">
        <BackgroundStars />
        
        <div className="tasks-content">
          {tasksVisible ? (
            <>
              <ExclusiveSection
                onPromoClick={handleExclusiveTask}
                onPrimaryTaskClick={handleExclusiveTask}
                onSecondaryTaskClick={handleExclusiveEarnings}
              />

              <h2 className="section-title">Доступные задания</h2>

              <TaskCard
                type="regular"
                title="Смотреть рекламу"
                description="Просмотрите короткую рекламу и получите 10 звезд"
                reward={10}
                buttonText="СМОТРЕТЬ"
                onClick={handleWatchAd}
              />

              {gigaOfferwallAvailable && (
                <TaskCard
                  type="regular"
                  title="Задания GigaOfferwall"
                  description="Выполняйте задания в офферволле Giga и получайте награды"
                  reward={0}
                  buttonText="ОТКРЫТЬ"
                  onClick={async () => {
                    try {
                      if (gigaOfferwallRef.current && typeof gigaOfferwallRef.current.open === 'function') {
                        await gigaOfferwallRef.current.open();
                      }
                    } catch (e) {}
                  }}
                />
              )}

              {monetagPopupAvailable && (
                <TaskCard
                  type="regular"
                  title="Переход по офферу"
                  description="Перейдите по офферу партнера и получите 10 звезд"
                  reward={10}
                  buttonText="ПЕРЕЙТИ"
                  onClick={handleMonetagPopupTask}
                />
              )}
              
              {/* Отображаем все задания с помощью универсального компонента по мере готовности каждого источника */}
              {/* Taddy */}
              {taddyTasks.map((task) => (
                <UniversalTaskCard 
                  key={`taddy-${task.id}`} 
                  taskType="taddy"
                  task={task} 
                  onComplete={() => reloadTaddyTasks()} 
                />
              ))}
              {isLoadingTaddy && taddyTasks.length === 0 && (
                <div className="tasks-loading">Загрузка заданий...</div>
              )}
              
              {/* Flayer */}
              {flayerTasks.map((task) => (
                <UniversalTaskCard 
                  key={`flayer-${task.signature}`} 
                  taskType="flayer"
                  task={task} 
                  onComplete={handleFlayerTaskComplete} 
                />
              ))}
              {isLoadingFlayer && flayerTasks.length === 0 && (
                <div className="tasks-loading">Загрузка заданий...</div>
              )}
              
              {/* Subgram */}
              {subgramTasks
                .filter((task) => !recentCompletedSubgram.has(task.link))
                .map((task) => (
                <UniversalTaskCard 
                  key={`subgram-${task.link}`} 
                  taskType="subgram"
                  task={task} 
                  onComplete={() => handleSubgramTaskComplete(task)} 
                />
              ))}
              {isLoadingSubgram && subgramTasks.length === 0 && (
                <div className="tasks-loading">Загрузка заданий...</div>
              )}
              
              {/* Traffy */}
              {traffyTasks.map((task) => (
                <UniversalTaskCard 
                  key={`traffy-${task.id}`} 
                  taskType="traffy"
                  task={task} 
                  onComplete={() => reloadTraffyTasks()} 
                />
              ))}
              {isLoadingTraffy && traffyTasks.length === 0 && (
                <div className="tasks-loading">Загрузка заданий...</div>
              )}
              
              {/* Tgrass */}
              {tgrassTasks.map((task) => (
                <UniversalTaskCard 
                  key={`tgrass-${task.link}`} 
                  taskType="tgrass"
                  task={task} 
                  onComplete={() => reloadTgrassTasks()} 
                />
              ))}
              {isLoadingTgrass && tgrassTasks.length === 0 && (
                <div className="tasks-loading">Загрузка заданий...</div>
              )}
              
              {/* Пользовательские задания - в самом конце */}
              {customTasks.map((task) => (
                <UniversalTaskCard 
                  key={`custom-${task.id}`} 
                  taskType="custom"
                  task={task} 
                  onComplete={() => reloadCustomTasks()} 
                />
              ))}
              {isLoadingCustom && customTasks.length === 0 && (
                <div className="tasks-loading">Загрузка пользовательских заданий...</div>
              )}

              {/* Общая заглушка, если ничего не грузится и задач нет */}
              {!isAnyLoading && !hasAnyTasks && (
                <div className="no-tasks-message">
                  На данный момент нет доступных заданий. Пожалуйста, проверьте позже.
                </div>
              )}
            </>
          ) : (
            <h2 className="section-title">Загрузка заданий...</h2>
          )}
        </div>
        
        {/* Модальное окно для уведомлений */}
        <CustomModal
          isOpen={modalIsOpen}
          title={modalConfig.title}
          message={modalConfig.message}
          buttons={modalConfig.buttons}
          onClose={(buttonId) => modalConfig.onClose(buttonId)}
        />
        
        {/* Модальное окно выбора пола временно отключено */}
        {false && (
          <GenderModal
            isOpen={genderModalOpen}
            onSelect={handleGenderSelect}
          />
        )}
        
        {/* Всплывающее окно с наградой за оформление доступа к курсам */}
        <RewardPopup 
          isOpen={showExclusiveReward}
          reward={250}
          onClose={handleCloseExclusiveReward}
        />

        <RewardPopup 
          isOpen={adRewardOpen}
          reward={adRewardAmount}
          onClose={() => setAdRewardOpen(false)}
        />
      </div>

      <NavigationBar />
    </div>
  );
};

export default TasksPage; 